import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { BarChart3, Blocks, Database, Brain, Search, BookOpen } from 'lucide-react';
import styles from './KnowledgeSidebar.module.css';

interface KnowledgeSidebarProps {
  title?: string;
  subtitle?: string;
  headerAction?: React.ReactNode;
  children?: React.ReactNode;
}

export const KnowledgeSidebar: React.FC<KnowledgeSidebarProps> = ({ 
  title = "Knowledge Hub", 
  subtitle = "Manage intelligence",
  headerAction,
  children 
}) => {
  const location = useLocation();

  const navItems = [
    { path: '/knowledge', label: 'Overview', icon: <BarChart3 size={16} />, exact: true },
    { path: '/knowledge/integrations', label: 'Integrations', icon: <Blocks size={16} /> },
    { path: '/knowledge/datasources', label: 'Data Sources', icon: <Database size={16} /> },
    // Use query params for minor tabs inside knowledge base page itself
    { path: '/knowledge?tab=questions', label: 'Questions & Audit', icon: <BookOpen size={16} /> },
    { path: '/knowledge?tab=models', label: 'Embedding Models', icon: <Brain size={16} /> },
    { path: '/knowledge?tab=search', label: 'Search & Test', icon: <Search size={16} /> },
  ];

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarHeaderTop}>
          <div>
            <h1 className={styles.sidebarTitle}>{title}</h1>
            {subtitle && <p className={styles.sidebarSubtitle}>{subtitle}</p>}
          </div>
          {headerAction && <div className={styles.headerAction}>{headerAction}</div>}
        </div>
      </div>

      <nav className={styles.navMenu}>
        <h3 className={styles.sectionTitle}>MENU</h3>
        {navItems.map((item) => {
          // If the item path has a query param, match accordingly, else exact or startsWith
          const isQueryNav = item.path.includes('?');
          let isActive = false;
          
          if (isQueryNav) {
             isActive = location.search === item.path.split('?')[1] || location.search.includes(item.path.split('?')[1]);
          } else {
             isActive = item.exact 
               ? location.pathname === item.path && !location.search 
               : location.pathname.startsWith(item.path);
          }

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={`${styles.navItem} ${isActive ? styles.active : ''}`}
            >
              <span className={styles.icon}>{item.icon}</span>
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {children && (
        <div className={styles.pageContextWrapper}>
          <div className={styles.divider} />
          {children}
        </div>
      )}
    </div>
  );
};
